import { debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { SortUtils } from '../../utils/sort-utils';
import { SnackBarService } from 'src/app/core/services/snackbar.service';
import { ServerErrorUtils } from '../../utils/server-error-utils';
import { Segment } from '../../domain/supply-marketplace.model';
import { ActiveArchivedColorMap } from 'src/app/shared/enums/active-archived.enum';
import { SupplyMarketplaceService } from '../../services/supply-marketplace.service';

@Component({
  selector: 'ctv-segment-autocomplete',
  template: ` <mat-form-field
    [floatLabel]="floatLabel || 'always'"
    class="w-100"
    [ngClass]="formFieldClass"
    [appearance]="formFieldAppearance"
  >
    <mat-label>{{label ? label : 'Select segment'}}</mat-label>
    <input
      matInput
      #search
      aria-label="Segment"
      [matAutocomplete]="auto"
      [formControl]="filterCtrl"
      [placeholder]="placeholder ? placeholder : 'Search'"
      [required]="required"
    />
    <i class="far fa-spin fa-spinner text-primary" matSuffix [hidden]="!loading"></i>
    <mat-autocomplete
      #auto="matAutocomplete"
      (optionSelected)="optionSelected($event)"
      [displayWith]="display"
    >
      <mat-option *ngFor="let item of filteredItems | async" [value]="item">
        <i class="fas fa-circle status-circle small" ngClass="text-{{SegmentStatusColorMap[item.status]}}"></i>
        {{ item.name }}
      </mat-option>
    </mat-autocomplete>
  </mat-form-field>`,
})
export class SegmentAutocompleteComponent implements OnInit, OnDestroy {
  @ViewChild('search', { static: true }) searchFilter: ElementRef;
  private readonly onDestroy = new Subject<void>();

  @Input() public placeholder: string;
  @Input() public floatLabel: string;
  @Input() public label: string;
  @Input() public formFieldClass: string;
  @Input() public formFieldAppearance: string;
  @Input() required: boolean;
  @Input() preselectedSegmentId: Segment['id'];
  @Output() segmentSelected = new EventEmitter<Segment>();

  filteredItems: Subject<Segment[]>; //List of segments to show in dropdown filtered with 'filteredSegmentIds'
  filterCtrl = new UntypedFormControl();
  segmentList: Segment[] = []; //stores currently fetched list of segments
  selectedSegment: Segment; //currently selected segment
  filteredSegmentIds: string[] = []; //It stores all the Id's of selected segments
  public loading = false;
  SegmentStatusColorMap = ActiveArchivedColorMap;

  constructor(
    private service: SupplyMarketplaceService,
    private snackBarService: SnackBarService) {
    this.filteredItems = new Subject();
    this.filterCtrl.valueChanges
      .pipe(debounceTime(800), distinctUntilChanged(), takeUntil(this.onDestroy))
      .subscribe(
        (searchTerm) => {
          if (searchTerm && typeof searchTerm === 'string') {
            this.getSegments(searchTerm);
          }
          else if (searchTerm === '') { //triggers when user deletes everything from input
            this.filteredItems.next();
            this.segmentSelected.emit(null);
          }
        }
      );
  }

  ngOnInit(): void {
    if (this.preselectedSegmentId) {
      this.getSegments('', this.preselectedSegmentId);
    }
  }

  /** SearchTerm used for filtering by segment name. PreselectedSegmentId used to get only one segment (preselected) at the component initialization. */
  private getSegments(searchTerm?: string, preselectedPubId?: string) {
    this.loading = true;
    this.filterCtrl.disable();
    return this.service.getSegments({
      page: 0,
      size: 1000,
      name: searchTerm,
      // ids: [preselectedPubId] NOT IMPLEMENTED YET ON BE
    } as any)
      .pipe(
        finalize(() => {
          this.filterCtrl.enable();
          !preselectedPubId ? this.searchFilter.nativeElement.focus() : 0; //No need for focus when loading page with preselected segment
          this.loading = false;
        }),
        takeUntil(this.onDestroy)
      )
      .subscribe(
        (resp) => {
          this.segmentList = resp.content.sort(SortUtils.propertyComparatorString('name'));
          if (preselectedPubId) {
            this.selectedSegment = this.segmentList[0]; //Naturally since the api call is filtered with one ID, selectedSegment will actually be the first one from the response
            this.filterCtrl.setValue(this.selectedSegment);
            this.updateFilteredSegments([this.selectedSegment]);
            this.emit();
          }
          this.filteredItems.next(this.segmentList.filter(x => !this.filteredSegmentIds.includes(x.id)));
        },
        (error) => {
          const messages = ServerErrorUtils.getValidationMessages(error);
          if (messages) {
            messages.forEach((m) => this.snackBarService.error(m));
          } else {
            this.snackBarService.error('Error while fetching publisher autocomplete data');
          }
        },
      );
  }

  public optionSelected($event) {
    this.selectedSegment = $event.option.value;
    this.emit();
    this.searchFilter.nativeElement.blur(); //blur == unfocus
    this.filteredItems.next(); //Reset list since the input value of autocomplete is empty and there is no point in showing filtered list on next focus on element
  }

  /** Emits value back to parent component.
   * If wrapped inside chips autocomplete it emits value to chips component that forwards values to parent component. */
  private emit() {
    this.segmentSelected.emit(this.selectedSegment);
  }

  /** Trigger only when using chips autocomplete. Triggered when item selected through chips autocomplete to update filteredSegmentIds. */
  public updateFilteredSegments(items) {
    this.filteredSegmentIds = items.map((item) => item.id);
  }

  /** Returns wanted object to input from whole selected item model. */
  public display(p?: Segment) {
    return p ? p.name : undefined;
  }

  /** Used only by chips autocomplete component to clear the input from selected item*/
  public resetInput() {
    this.filterCtrl.setValue(null);
    this.filteredItems.next(); //Reset list since the input value of autocomplete is empty
  }

  public disable() {
    this.filterCtrl.disable();
  }

  public enable() {
    this.filterCtrl.enable();
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
  }
}
